import styled from '@emotion/styled'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import React, { useState } from 'react'
import Label from '../../components/form/Label'
import RichTextEditor from '../../components/global/form/RichTextEditor'
import { StyledButton as AdminButton } from '../admin/Login'
import { AdminForm } from '../admin/users/AdminUsersForm'

export type Intensity = 'LOW' | 'MEDIUM' | 'HIGH'

interface SkillTag {
    id: number
    name: string
}

interface ExerciseFormProps {
    initial?: {
        title: string
        description: string
        peopleCount: number
        intensity: Intensity
        skillIds: number[]
    }
    skills: SkillTag[]
    loading?: boolean
    onSubmit: (data: any) => void
    onCancel: () => void
}

export default function ExerciseForm({ initial, skills, loading, onSubmit, onCancel }: Readonly<ExerciseFormProps>) {
    const [form, setForm] = useState({
        title: initial?.title ?? '',
        description: initial?.description ?? '',
        peopleCount: initial?.peopleCount ?? 1,
        intensity: initial?.intensity ?? 'LOW',
        skillIds: initial?.skillIds ?? [],
    })
    const [error, setError] = useState<string | null>(null)

    return (
        <AdminForm
            style={{ minWidth: 320, gap: 18 }}
            onSubmit={(e) => {
                e.preventDefault()
                if (!form.skillIds || form.skillIds.length === 0) {
                    setError('Merci de sélectionner au moins une compétence.')
                    return
                }
                setError(null)
                onSubmit(form)
            }}
        >
            <Label>Titre</Label>
            <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="form-control"
                required
            />
            <Label>Description</Label>
            <RichTextEditor
                value={form.description ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            />

            <Label>Nombre de personnes</Label>
            <input
                type="number"
                min={1}
                value={form.peopleCount}
                onChange={(e) => setForm((f) => ({ ...f, peopleCount: +e.target.value }))}
                className="form-control"
                required
            />
            <Label>Intensité</Label>
            <select
                value={form.intensity}
                onChange={(e) => setForm((f) => ({ ...f, intensity: e.target.value as Intensity }))}
                className="form-select"
                required
            >
                <option value="LOW">Basse</option>
                <option value="MEDIUM">Moyenne</option>
                <option value="HIGH">Haute</option>
            </select>
            <Label>Compétences</Label>
            <Autocomplete
                multiple
                options={skills}
                getOptionLabel={(option) => option.name}
                value={skills.filter((skill) => form.skillIds.includes(skill.id))}
                onChange={(_, value) => setForm((f) => ({ ...f, skillIds: value.map((v) => v.id) }))}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" placeholder="Sélectionnez des compétences" />
                )}
                sx={{ background: 'white', borderRadius: 1 }}
            />
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <ButtonBar>
                <AdminButton type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Enregistrement...' : 'Valider'}
                </AdminButton>
            </ButtonBar>
        </AdminForm>
    )
}

const ButtonBar = styled.div`
    display: flex;
    gap: 18px;
    margin-top: 10px;
`

const ErrorMsg = styled.div`
    color: #d32f2f;
    margin-top: 8px;
`
